import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, Trash2, Calendar, Clock, Hash } from "lucide-react";
import { useNavigate } from "react-router-dom";
import logo from "@/assets/logo_salone_vincente.png";

interface SubmissionLog {
  id: string;
  timestamp: number;
  groupId: string;
  Id: string;
}

const SubmissionLogs = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<SubmissionLog[]>([]);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = () => {
    try {
      const lastSubmission = localStorage.getItem('lastSubmission');
      if (lastSubmission) {
        const parsedLog = JSON.parse(lastSubmission);
        setLogs([parsedLog]);
      }
    } catch (error) {
      console.error("Errore nel caricamento dei log:", error);
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const clearLogs = () => {
    if (window.confirm("Sei sicuro di voler cancellare tutti i log?")) {
      localStorage.removeItem('lastSubmission');
      setLogs([]);
    }
  };

  return (
    <div className="min-h-screen gradient-subtle py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8 animate-fade-in">
          <img src={logo} alt="Salone Vincente" className="w-32 h-32 object-contain mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent mb-2">
            Storico Submission
          </h1>
          <p className="text-muted-foreground">
            Visualizza i log dei webhook inviati
          </p>
        </div>

        <div className="flex gap-4 mb-6">
          <Button 
            variant="outline" 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Indietro
          </Button>
          
          {logs.length > 0 && (
            <Button 
              variant="destructive" 
              onClick={clearLogs}
              className="flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Cancella Log
            </Button>
          )}
        </div>

        {logs.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="text-6xl mb-4">📋</div>
            <h2 className="text-xl font-semibold mb-2">Nessuna submission trovata</h2>
            <p className="text-muted-foreground">
              I log delle submission appariranno qui dopo l'invio del modulo
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {logs.map((log) => (
              <Card key={log.id} className="p-6 shadow-elegant animate-slide-up">
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <Badge className="mb-3" variant="default">
                        Submission Inviata
                      </Badge>
                      
                      <div className="grid gap-3 md:grid-cols-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">Data:</span>
                          <span className="text-muted-foreground">{formatDate(log.timestamp)}</span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">Ora:</span>
                          <span className="text-muted-foreground">{formatTime(log.timestamp)}</span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm md:col-span-2">
                          <Hash className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">ID Submission:</span>
                          <code className="text-xs bg-muted px-2 py-1 rounded">{log.id}</code>
                        </div>
                        
                        {log.groupId && (
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-medium">Group ID:</span>
                            <span className="text-muted-foreground">{log.groupId}</span>
                          </div>
                        )}
                        
                        {log.Id && (
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-medium">ID:</span>
                            <span className="text-muted-foreground">{log.Id}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t">
                    <p className="text-xs text-muted-foreground">
                      Webhook inviato a: n8n.srv1035791.hstgr.cloud
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmissionLogs;
